import type { FC } from 'react';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import Dota2HeroPick from 'app/components/Dota2HeroPick';
import Dota2Map from 'app/components/Dota2Map';
import GoldAdvantageChart from 'app/components/GoldAdvantageChart';
import { partitionArray } from 'app/utils/array';
import sharedStyle from 'app/components/MatchesRecap/style.module.scss';
import ConfigContext from 'app/common/ConfigContext';
import InterGameMessage from 'app/components/Blocks/NameBlock/InterGameMessage';
import { getScoreboardTypeForVideogame } from 'app/config/urlParams.config.helper';

import style from './style.module.scss';

interface Props {
  game: LiveGameDota2;
  opponent1: OpponentBase | undefined;
  opponent2: OpponentBase | undefined;
  matchStatus: EventStatus;
}

const Dota2LiveScoreboard: FC<Props> = ({ game, opponent1, opponent2, matchStatus }) => {
  const { t } = useTranslation();
  const { scoreboard } = useContext(ConfigContext);

  const scoreboardType = getScoreboardTypeForVideogame(scoreboard, game.videogame_slug);

  const minimapScoreboard = scoreboardType === 'minimap';
  const basicScoreboard = scoreboardType === 'basic';

  const [dires, radiants] = partitionArray(game.opponents, opponent =>
    opponent.side === 'dire' ? 0 : 1
  );
  const dire = dires.at(0);
  const radiant = radiants.at(0);
  const [gameOpponents1, gameOpponents2] = partitionArray(game.opponents, opponent =>
    opponent.id === opponent1?.id ? 0 : 1
  );
  const gameOpponent1 = gameOpponents1.at(0);
  const gameOpponent2 = gameOpponents2.at(0);

  const winner = game.opponents.find(opponent => opponent.id === game.winner_id);

  const [direOpponentInfo, radiantOpponentInfo] =
    opponent1?.id === dire?.id ? [opponent1, opponent2] : [opponent2, opponent1];

  const winnerInfo = [opponent1, opponent2].find(opponent => opponent?.id === game.winner_id);

  const hasData = Boolean(
    dire?.tower_status ||
      radiant?.tower_status ||
      (gameOpponent1?.heroes ?? []).length > 0 ||
      (gameOpponent2?.heroes ?? []).length > 0 ||
      (game.radiant_gold_adv ?? []).length > 0
  );

  if (basicScoreboard) {
    return (
      <InterGameMessage
        scoreboardGame={game}
        matchStatus={matchStatus}
        hasLiveStats={hasData}
        winnerInfo={winnerInfo}
        winnerSide={winner?.side ?? null}
        size="small"
        videogameSlug="dota-2"
      />
    );
  }

  return (
    <div
      className={classNames(style.dota2LiveScoreboard, {
        [style.minimapScoreboard]: minimapScoreboard
      })}
    >
      <div className={classNames(style.mapAndPicksContainer)}>
        {!minimapScoreboard && (
          <h4 className={sharedStyle.statsContainerTitle}>{t('component_match-recap_moba_map')}</h4>
        )}
        <div
          className={classNames(
            style.heroesWinrateTitle,
            'override-scoreboard_heroes_winrate_title'
          )}
        >
          {(gameOpponent1?.heroes ?? []).length > 0 && (
            <div>{t('blocks_name_hero-winrates-percentage')}</div>
          )}
          {(gameOpponent2?.heroes ?? []).length > 0 && (
            <div>{t('blocks_name_hero-winrates-percentage')}</div>
          )}
        </div>
        <div className={style.mapContainer}>
          <div
            className={classNames(style.heroesContainer, 'override-scoreboard_heroes_container')}
          >
            {gameOpponent1?.heroes?.map((hero, index) => (
              <Dota2HeroPick
                key={`gameOpponent1_${index}`}
                hero={hero}
                side={gameOpponent1.side}
                orientation="normal"
                withTooltip={!minimapScoreboard}
              />
            ))}
          </div>
          <Dota2Map
            dire={dire}
            radiant={radiant}
            winner={winner?.side}
            className={classNames(style.map, 'override-scoreboard_map')}
          />
          <InterGameMessage
            scoreboardGame={game}
            matchStatus={matchStatus}
            hasLiveStats={hasData}
            winnerInfo={winnerInfo}
            winnerSide={winner?.side ?? null}
            size="large"
            videogameSlug="dota-2"
          />
          <div
            className={classNames(style.heroesContainer, 'override-scoreboard_heroes_container')}
          >
            {gameOpponent2?.heroes?.map((hero, index) => (
              <Dota2HeroPick
                key={`gameOpponent2_${index}`}
                hero={hero}
                side={gameOpponent2.side}
                orientation="reversed"
                withTooltip={!minimapScoreboard}
              />
            ))}
          </div>
        </div>
      </div>

      {!minimapScoreboard && (
        <GoldAdvantageChart
          goldAdvantageTeamInfo={radiantOpponentInfo}
          secondTeamInfo={direOpponentInfo}
          goldAvantageTeam="radiant"
          goldAdvantage={game.radiant_gold_adv ?? []}
        />
      )}
    </div>
  );
};

Dota2LiveScoreboard.displayName = 'Dota2LiveScoreboard';

export default Dota2LiveScoreboard;
